import { vercel } from "@t3-oss/env-core/presets-zod";
import { createEnv } from "@t3-oss/env-nextjs";
import { z } from "zod";

export const env = createEnv({
  extends: [vercel()],
  isServer: typeof window === "undefined",
  client: {
    NEXT_PUBLIC_PORT: z.string(),
    NEXT_PUBLIC_BASE_URL: z.string(),
    NEXT_PUBLIC_BASE_PATH: z.string().optional(),
    NEXT_PUBLIC_ASSET_URL: z.string(),
    NEXT_PUBLIC_ROOT_DOMAIN: z.string(),
    NEXT_PUBLIC_I18N: z.string(),
    NEXT_PUBLIC_GOOGLE_VERIFICATION: z.string().optional(),
    // Features
    NEXT_PUBLIC_GA_TRACKING: z.stringbool().optional(),
    NEXT_PUBLIC_LOCATION_TITLE_FACET: z.stringbool().optional(),
    NEXT_PUBLIC_LOCATION_TREE_CONTINENT: z.stringbool().optional(),
    NEXT_PUBLIC_FILTER_LANGUAGE: z.stringbool().optional(),
    NEXT_PUBLIC_FILTER_ENTRY_LEVEL_MULTI: z.stringbool().optional(),
  },
  server: {
    BETTER_AUTH_URL: z.string(),
    BETTER_AUTH_SECRET: z.string(),
    BETTER_AUTH_DATABASE_URL: z.string(),
    JWT_SECRET: z.string(),
    TYPESENSE_HOST: z.string(),
    TYPESENSE_API_KEY: z.string(),
    // Search engine switch (tkag Atlas port). typesense = current behaviour.
    SEARCH_ENGINE: z.enum(["typesense", "atlas", "shadow"]).optional(),
    // Fraction of requests shadowed when SEARCH_ENGINE=shadow (0..1). Kept
    // as a string: `z.coerce.number()` rejects NaN at module init, so a typo
    // like "10%" would 500 every route of every tenant sharing this schema.
    // Parsed and clamped in db/search/engine.ts instead.
    SEARCH_SHADOW_SAMPLE: z.string().optional(),
    // Autocomplete fires per keystroke; its shadow has its own rate, default 0
    // (the final term is still compared by the query endpoint's shadow).
    SEARCH_SHADOW_AUTOCOMPLETE_SAMPLE: z.string().optional(),
    MONGODB_JOBS_URL: z.string(),
    MONGODB_JOBS_NAME: z.string(),
    MONGODB_UTILITY_NAME: z.string(),
    GEOAPIFY_APIKEY: z.string(),
    TRANSLATIONS_TABLE_NAME: z.string(),
    TRANSLATIONS_SHEET_SHARE_LINK: z.string(),
    GOOGLE_SHEET_ID: z.string(),
    GOOGLE_CLIENT_EMAIL: z.string(),
    PRIVATE_KEY_TRANSLATE: z.string(),
    CRON_SECRET: z.string(),
    OPENAI_API_KEY: z.string(),
    SKILL_MATCHER_API_KEY: z.string().optional(),
    SKILL_MATCHER_SESSION_SECRET: z.string().min(32).optional(),
    SKILL_MATCHER_TURNSTILE_ENABLED: z.stringbool().optional(),
    TURNSTILE_SECRET_KEY: z.string().optional(),

    MONGODB_NEWSLETTER_URL: z.string(),
    MONGODB_NEWSLETTER_NAME: z.string(),
    MONGODB_NEWSLETTER_COLLECTION: z.string(),

    NEXT_PRIVATE_DEBUG_CACHE: z.string().optional(),
    // used for a special case in getBaseURL for staging branches with a custom domain
    STAGING_BASE_URL_OVERRIDE: z.string().optional(),
    // Optional shared-password gate. When set (per Vercel environment), the
    // middleware password-gate is active; unset → no-op. See lib/middleware/password-gate.
    PASSWORD: z.string().optional(),
  },
  shared: {
    CLIENT: z.string().min(1),
    ENVIRONMENT: z.enum(["production", "staging", "development"]),
  },
  experimental__runtimeEnv: {
    CLIENT: process.env.CLIENT,
    ENVIRONMENT: process.env.ENVIRONMENT,
    NEXT_PUBLIC_I18N: process.env.NEXT_PUBLIC_I18N,
    NEXT_PUBLIC_GOOGLE_VERIFICATION:
      process.env.NEXT_PUBLIC_GOOGLE_VERIFICATION,
    NEXT_PUBLIC_PORT: process.env.NEXT_PUBLIC_PORT,
    NEXT_PUBLIC_BASE_URL: process.env.NEXT_PUBLIC_BASE_URL,
    NEXT_PUBLIC_BASE_PATH: process.env.NEXT_PUBLIC_BASE_PATH,
    NEXT_PUBLIC_ASSET_URL: process.env.NEXT_PUBLIC_ASSET_URL,
    NEXT_PUBLIC_FILTER_LANGUAGE: process.env.NEXT_PUBLIC_FILTER_LANGUAGE,
    NEXT_PUBLIC_FILTER_ENTRY_LEVEL_MULTI:
      process.env.NEXT_PUBLIC_FILTER_ENTRY_LEVEL_MULTI,
    NEXT_PUBLIC_GA_TRACKING: process.env.NEXT_PUBLIC_GA_TRACKING,
    NEXT_PUBLIC_LOCATION_TREE_CONTINENT:
      process.env.NEXT_PUBLIC_LOCATION_TREE_CONTINENT,
    NEXT_PUBLIC_LOCATION_TITLE_FACET:
      process.env.NEXT_PUBLIC_LOCATION_TITLE_FACET,
    NEXT_PUBLIC_ROOT_DOMAIN: process.env.NEXT_PUBLIC_ROOT_DOMAIN,
  },
});
